import {
  ComplianceObligation,
  ComplianceObligationInstance,
  ObligationEvidenceRecord,
  ObligationWaiverExtension,
  ObligationsDashboardMetrics,
} from './types';

const API_BASE = '/api/compliance/obligations';
const DEFAULT_ORG = 'ORG-XYZ';

function getHeaders() {
  return {
    'Content-Type': 'application/json',
    'x-organization-id': DEFAULT_ORG,
  };
}

export const obligationsService = {
  // Metrics
  async getMetrics(): Promise<ObligationsDashboardMetrics> {
    const res = await fetch(`${API_BASE}/metrics`, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load obligation dashboard metrics');
    return res.json();
  },

  // Masters
  async getObligations(filters?: { status?: string; department?: string; priority?: string }): Promise<ComplianceObligation[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.department) params.append('department', filters.department);
    if (filters?.priority) params.append('priority', filters.priority);

    const url = params.toString() ? `${API_BASE}/masters?${params.toString()}` : `${API_BASE}/masters`;
    const res = await fetch(url, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load compliance obligations');
    return res.json();
  },

  async getObligationById(id: string): Promise<ComplianceObligation> {
    const res = await fetch(`${API_BASE}/masters/${id}`, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load obligation details');
    return res.json();
  },

  async createObligation(data: Partial<ComplianceObligation>): Promise<ComplianceObligation> {
    const res = await fetch(`${API_BASE}/masters`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create compliance obligation');
    return res.json();
  },

  async updateObligation(id: string, data: Partial<ComplianceObligation>): Promise<ComplianceObligation> {
    const res = await fetch(`${API_BASE}/masters/${id}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update compliance obligation');
    return res.json();
  },

  // Instances
  async getInstances(filters?: { obligation_id?: string; lifecycle_status?: string; is_overdue?: boolean }): Promise<ComplianceObligationInstance[]> {
    const params = new URLSearchParams();
    if (filters?.obligation_id) params.append('obligation_id', filters.obligation_id);
    if (filters?.lifecycle_status) params.append('lifecycle_status', filters.lifecycle_status);
    if (filters?.is_overdue !== undefined) params.append('is_overdue', String(filters.is_overdue));

    const url = params.toString() ? `${API_BASE}/instances?${params.toString()}` : `${API_BASE}/instances`;
    const res = await fetch(url, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load obligation instances');
    return res.json();
  },

  async getInstanceById(id: string): Promise<ComplianceObligationInstance> {
    const res = await fetch(`${API_BASE}/instances/${id}`, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load instance details');
    return res.json();
  },

  async createInstance(data: Partial<ComplianceObligationInstance>): Promise<ComplianceObligationInstance> {
    const res = await fetch(`${API_BASE}/instances`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create obligation instance');
    return res.json();
  },

  async fulfillInstance(id: string, notes: string, completedBy: string): Promise<ComplianceObligationInstance> {
    const res = await fetch(`${API_BASE}/instances/${id}/fulfill`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ fulfillment_notes: notes, completed_by: completedBy }),
    });
    if (!res.ok) throw new Error('Failed to fulfill obligation instance');
    return res.json();
  },

  async verifyInstance(
    id: string,
    data: {
      verification_status: 'PASSED' | 'FAILED' | 'PARTIAL';
      verifier_id: string;
      verifier_name: string;
      verification_notes?: string;
    }
  ): Promise<ComplianceObligationInstance> {
    const res = await fetch(`${API_BASE}/instances/${id}/verify`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to verify obligation instance');
    return res.json();
  },

  // Evidence
  async addEvidence(
    instanceId: string,
    data: {
      title: string;
      file_name: string;
      file_type: string;
      file_size_bytes?: number;
      uploaded_by: string;
      notes?: string;
    }
  ): Promise<ObligationEvidenceRecord> {
    const res = await fetch(`${API_BASE}/instances/${instanceId}/evidence`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to attach evidence');
    return res.json();
  },

  // Waivers & Extensions
  async requestWaiver(
    instanceId: string,
    data: {
      request_type: 'EXTENSION' | 'WAIVER';
      requested_due_date?: string;
      justification: string;
      compensating_controls?: string;
      requested_by: string;
    }
  ): Promise<ObligationWaiverExtension> {
    const res = await fetch(`${API_BASE}/instances/${instanceId}/waivers`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to request waiver/extension');
    return res.json();
  },

  async approveWaiver(waiverId: string, approvedBy: string, notes?: string): Promise<ObligationWaiverExtension> {
    const res = await fetch(`${API_BASE}/waivers/${waiverId}/approve`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ approved_by: approvedBy, approval_notes: notes }),
    });
    if (!res.ok) throw new Error('Failed to approve waiver/extension');
    return res.json();
  },
};
